/**
 * UserManager - 用户管理器
 * 
 * 管理用户身份、认证、会话
 * 注：现在使用 UID 作为主键，不再使用 userId
 */

import type { 
  User, 
  UserSession, 
  LoginCredentials, 
  RegisterData,
  UserWithAccount,
  UserSettings
} from '../types/user.types';
import { UserRole as Role } from '../types/user.types';
import type { AccountManager } from './AccountManager';
import { TradingSettingsManager } from './TradingSettingsManager';
import { BrokerClientIndexManager } from './BrokerClientIndexManager';
import { ReferralCodeManager } from './ReferralCodeManager';
import { ReferrerIndexManager } from './ReferrerIndexManager';
import { logger } from '../utils/logger';
import { storage } from '../utils/storage';

const DEFAULT_BROKER_UID = '10000001';  // 默认交易商UID
const DEFAULT_BROKER_NAME = '系统默认交易商';  // 默认交易商名称

export class UserManager {
  private users: Map<string, User>;  // 用 UID 作为键
  private currentSession: UserSession | null;
  private accountManager: AccountManager;
  private tradingSettingsManager: TradingSettingsManager;
  private brokerClientIndexManager: BrokerClientIndexManager;
  private referralCodeManager: ReferralCodeManager;
  private referrerIndexManager: ReferrerIndexManager;

  constructor(accountManager: AccountManager) {
    this.users = new Map();
    this.currentSession = null;
    this.accountManager = accountManager;
    this.tradingSettingsManager = new TradingSettingsManager();
    this.brokerClientIndexManager = new BrokerClientIndexManager();
    this.referralCodeManager = new ReferralCodeManager();
    this.referrerIndexManager = new ReferrerIndexManager();
    
    this.loadUsers();
    this.createDefaultAccounts(); // 创建默认测试账号
    this.restoreSession();
    
    logger.log('UserManager initialized');
  }

  /**
   * 用户注册
   */
  register(data: RegisterData): User {
    // 验证用户名是否已存在
    if (this.getUserByUsername(data.username)) {
      throw new Error('用户名已存在');
    }

    // 验证邮箱是否已存在
    if (this.getUserByEmail(data.email)) {
      throw new Error('邮箱已被使用');
    }

    // 处理交易商：如果提供了 brokerUid，验证其是否存在
    let brokerUid = data.brokerUid?.trim() || '';
    let brokerName = DEFAULT_BROKER_NAME;
    
    if (brokerUid) {
      // 验证交易商是否存在（通过 UID 查找用户）
      const brokerUser = this.getUserByUID(brokerUid);
      if (!brokerUser) {
        throw new Error(`交易商UID不存在: ${brokerUid}`);
      }
      if (brokerUser.role !== Role.BROKER) {
        throw new Error(`该UID对应的用户不是交易商: ${brokerUid}`);
      }
      brokerName = brokerUser.username;
      logger.log(`✅ Broker verified: UID=${brokerUid}, name=${brokerName}`);
    } else {
      // 未提供交易商UID，使用默认
      brokerUid = DEFAULT_BROKER_UID;
      logger.log(`ℹ️ Using default broker: ${brokerUid}`);
    }

    const uid = this.generateUID();  // 生成UID作为主键
    
    // 生成推荐码
    const referralCode = this.referralCodeManager.generateReferralCode();
    
    // 处理推荐人
    let referrerUid: string | undefined;
    if (data.referrerCode) {
      // 1. 尝试作为推荐码查找
      const foundUid = this.referralCodeManager.getUidByCode(data.referrerCode);
      
      if (foundUid) {
        referrerUid = foundUid;
        logger.log(`✅ Referrer found by code: ${data.referrerCode} → ${referrerUid}`);
      } else {
        // 2. 如果推荐码不存在，尝试作为 UID 查找（兼容旧系统）
        if (/^\d{8}$/.test(data.referrerCode)) {
          const referrerUser = this.getUserByUID(data.referrerCode);
          if (referrerUser) {
            referrerUid = data.referrerCode;
            logger.log(`✅ Referrer found by UID (legacy): ${referrerUid}`);
          } else {
            logger.warn(`Invalid referrer code (not found as code or UID): ${data.referrerCode}`);
          }
        } else {
          logger.warn(`Invalid referrer code: ${data.referrerCode}`);
        }
      }
    }
    
    // 创建用户
    const user: User = {
      uid,
      username: data.username,
      email: data.email,
      role: Role.TRADER,  // 默认角色为交易者
      accountId: uid,     // accountId 同 uid
      referralCode,       // 新增：推荐码
      customReferralCode: undefined,  // 新增：自定义推荐码（初始为空）
      profile: {
        referrerCode: data.referrerCode,  // 推荐人的推荐码
        referrerUid,                      // 推荐人的UID（内部使用）
        referrerLockedAt: referrerUid ? Date.now() : undefined,  // 如果有推荐人，立即锁定
        hasSeenReferrerPopup: referrerUid ? true : false,  // 如果注册时填写了推荐码，不再显示弹窗
        web3Address: undefined,          // Web3地址（后续绑定）
        googleAccount: undefined,        // Google账号（后续绑定）
      },
      createdAt: Date.now(),
      lastLoginAt: 0,
      settings: this.getDefaultSettings()
    };

    // 保存密码（简单哈希，生产环境应使用更安全的方法）
    this.savePassword(uid, data.password);

    // 保存用户
    this.users.set(uid, user);
    this.saveUser(user);

    // 创建账户
    this.accountManager.createAccount(uid, 10000);  // 初始10000 USDT

    // 创建交易设置（分配给指定的交易商）
    this.tradingSettingsManager.createDefaultSettings(uid, brokerUid, brokerName);
    
    // ✅ 添加到交易商客户索引
    this.brokerClientIndexManager.addClient(brokerUid, uid);
    
    // ✅ 注册推荐码
    this.referralCodeManager.registerCode(referralCode, uid);
    
    // ✅ 如果有推荐人，添加到推荐人索引
    if (referrerUid) {
      this.referrerIndexManager.addReferredUser(referrerUid, uid);
      logger.log(`✅ Referral relationship: ${referrerUid} → ${uid}`);
    }

    logger.log(`✅ User registered: ${data.username} (UID: ${uid}, Code: ${referralCode}) -> Broker: ${brokerUid} (${brokerName})`);
    return user;
  }

  /**
   * 用户登录
   */
  login(credentials: LoginCredentials): UserSession {
    // 查找用户
    const user = this.getUserByUsername(credentials.username);
    if (!user) {
      throw new Error('用户名或密码错误');
    }

    // 验证密码
    if (!this.verifyPassword(user.uid, credentials.password)) {
      throw new Error('用户名或密码错误');
    }

    // 更新最后登录时间
    user.lastLoginAt = Date.now();
    this.users.set(user.uid, user);
    this.saveUser(user);

    // 创建会话
    const session: UserSession = {
      sessionId: this.generateSessionId(),
      uid: user.uid,
      token: this.generateToken(),
      role: user.role,
      expiresAt: Date.now() + (credentials.remember ? 30 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000), // 30天 or 1天
      remember: credentials.remember || false
    };

    this.currentSession = session;
    this.saveSession(session);

    logger.log(`✅ User logged in: ${user.username} (UID: ${user.uid})`);
    return session;
  }

  /**
   * 游客模式
   */
  loginAsGuest(): UserSession {
    const guestUid = this.generateUID();
    
    // 创建临时游客用户
    const guestUser: User = {
      uid: guestUid,
      username: '游客',
      email: '',
      role: Role.GUEST,
      accountId: guestUid,
      referralCode: 'GUEST',  // 游客不需要推荐码
      profile: {},
      createdAt: Date.now(),
      lastLoginAt: Date.now(),
      settings: this.getDefaultSettings()
    };

    this.users.set(guestUid, guestUser);

    // 创建会话（不保存到 localStorage）
    const session: UserSession = {
      sessionId: this.generateSessionId(),
      uid: guestUid,
      token: this.generateToken(),
      role: Role.GUEST,
      expiresAt: Date.now() + 60 * 60 * 1000,  // 1小时
      remember: false
    };

    this.currentSession = session;

    logger.log(`👤 Guest login: UID ${guestUid}`);
    return session;
  }

  /**
   * 登出
   */
  logout(): void {
    if (this.currentSession) {
      logger.log(`👋 User logged out: ${this.currentSession.uid}`);
      
      // 如果是游客，删除临时用户
      if (this.currentSession.role === Role.GUEST) {
        this.users.delete(this.currentSession.uid);
      }
      
      this.currentSession = null;
      this.clearSession();
    }
  }

  /**
   * 获取当前会话
   */
  getCurrentSession(): UserSession | null {
    // 检查会话是否过期
    if (this.currentSession && this.currentSession.expiresAt < Date.now()) {
      console.warn('⚠️ [getCurrentSession] Session已过期');
      logger.warn('Session expired');
      this.logout();
      return null;
    }
    
    return this.currentSession;
  }

  /**
   * 获取当前用户
   */
  getCurrentUser(): User | null {
    const session = this.getCurrentSession();
    if (!session) {
      return null;
    }
    
    let user = this.users.get(session.uid);
    
    // ✅ 如果本地没有用户数据，尝试从session中恢复基本信息
    if (!user && session.username) {
      // 从session创建临时用户对象
      user = {
        uid: session.uid,
        username: session.username,
        email: '',
        role: session.role,
        accountId: session.uid,
        referralCode: '',
        createdAt: Date.now(),
        lastLoginAt: Date.now(),
      } as User;
      
      // 缓存到本地
      this.users.set(session.uid, user);
      logger.log(`✅ Created temporary user from session: ${user.username}`);
    }
    
    return user ?? null;
  }

  /**
   * 获取用户（含账户信息）
   */
  getUserWithAccount(uid: string): UserWithAccount {
    const user = this.users.get(uid);
    if (!user) {
      throw new Error(`User not found: ${uid}`);
    }

    const account = this.accountManager.getAccount(uid);

    return {
      ...user,
      account
    };
  }

  /**
   * 获取用户（仅基本信息）- 通过 UID
   */
  getUser(uid: string): User | null {
    return this.users.get(uid) || null;
  }

  /**
   * 通过 UID 查找用户
   */
  getUserByUID(uid: string): User | null {
    return this.users.get(uid) || null;
  }
  
  /**
   * 获取所有用户
   */
  getAllUsers(): User[] {
    return Array.from(this.users.values());
  }
  
  /**
   * 获取交易商客户索引管理器
   */
  getBrokerClientIndexManager(): BrokerClientIndexManager {
    return this.brokerClientIndexManager;
  }

  /**
   * 通过用户名查找用户
   */
  private getUserByUsername(username: string): User | null {
    for (const user of this.users.values()) {
      if (user.username === username) {
        return user;
      }
    }
    return null;
  }

  /**
   * 通过邮箱查找用户
   */
  private getUserByEmail(email: string): User | null {
    for (const user of this.users.values()) {
      if (user.email === email) {
        return user;
      }
    }
    return null;
  }

  /**
   * 更新用户设置
   */
  updateSettings(uid: string, settings: Partial<UserSettings>): void {
    const user = this.users.get(uid);
    if (!user) {
      throw new Error(`User not found: ${uid}`);
    }

    user.settings = { ...user.settings, ...settings } as UserSettings;
    this.users.set(uid, user);
    this.saveUser(user);

    logger.log(`⚙️ Updated settings for user ${uid}`);
  }

  /**
   * 更新用户角色
   */
  updateUserRole(uid: string, newRole: Role): boolean {
    const user = this.users.get(uid);
    if (!user) {
      logger.error(`User not found: ${uid}`);
      return false;
    }

    const oldRole = user.role;
    user.role = newRole;

    // 保存用户数据
    this.users.set(uid, user);
    this.saveUser(user);
    
    // 如果当前用户正在更改自己的角色，更新会话
    if (this.currentSession && this.currentSession.uid === uid) {
      this.currentSession = {
        ...this.currentSession,
        role: newRole
      };
      this.saveSession(this.currentSession);
    }

    logger.log(`User role updated: ${uid} from ${oldRole} to ${newRole}`);
    return true;
  }

  /**
   * 更新用户 Profile
   */
  updateUserProfile(uid: string, profileUpdates: Partial<User['profile']>): boolean {
    const user = this.users.get(uid);
    if (!user) {
      logger.error(`User not found: ${uid}`);
      return false;
    }

    // 更新 profile
    user.profile = {
      ...user.profile,
      ...profileUpdates
    };

    // 保存用户数据
    this.users.set(uid, user);
    this.saveUser(user);

    logger.log(`User profile updated: ${uid}`);
    return true;
  }

  /**
   * 检查权限
   */
  hasPermission(uid: string, _feature: string): boolean {
    const user = this.users.get(uid);
    
    if (!user) {
      // ✅ 修复：如果本地没有用户数据，但有有效的 session，则信任后端
      // 后端创建的用户不会自动同步到前端 localStorage
      // 只要有有效的 session token，就说明后端已验证通过
      if (this.currentSession && this.currentSession.uid === uid) {
        return true;
      }
      
      console.error('❌ [hasPermission] 用户不存在且无有效Session');
      return false;
    }

    // 管理员拥有所有权限
    if (user.role === Role.ADMIN) {
      return true;
    }

    // TODO: 根据 PERMISSIONS 配置检查权限
    return true;
  }

  /**
   * 创建默认测试账号
   */
  private createDefaultAccounts(): void {
    // 检查是否已创建默认账号（使用版本标记）
    const SCHEMA_VERSION = 'v2_uid_based'; // 新版本标记
    const schemaVersion = storage.get<string>('schema_version', '');
    
    if (schemaVersion === SCHEMA_VERSION) {
      // 已经是新版本，检查是否有默认账号
      const brokerExists = this.users.has('10000001');
      if (brokerExists) {
        return; // 默认账号已存在
      }
    } else {
      // 版本不匹配，清空旧数据
      logger.warn('⚠️ Schema version mismatch, clearing old data...');
      this.clearOldUserData();
      storage.set('schema_version', SCHEMA_VERSION);
    }

    try {
      // 创建 Broker 测试账号
      const brokerUid = '10000001';  // 固定UID for broker
      const brokerCode = this.referralCodeManager.generateReferralCode();
      const brokerUser: User = {
        uid: brokerUid,
        username: 'broker',
        email: 'broker@example.com',
        role: Role.BROKER,
        accountId: brokerUid,
        referralCode: brokerCode,
        profile: {
          hasSeenReferrerPopup: true, // 默认broker不显示弹窗
        },
        createdAt: Date.now(),
        lastLoginAt: 0,
        settings: this.getDefaultSettings(),
      };
      
      this.users.set(brokerUid, brokerUser);
      this.saveUser(brokerUser); // 保存broker用户
      this.savePassword(brokerUid, 'broker123');
      this.accountManager.createAccount(brokerUid, 100000); // 10万初始资金
      this.referralCodeManager.registerCode(brokerCode, brokerUid);

      // 创建 Admin 测试账号
      const adminUid = '10000000';  // 固定UID for admin
      const adminCode = this.referralCodeManager.generateReferralCode();
      const adminUser: User = {
        uid: adminUid,
        username: 'admin',
        email: 'admin@example.com',
        role: Role.ADMIN,
        accountId: adminUid,
        referralCode: adminCode,
        profile: {
          hasSeenReferrerPopup: true, // 默认admin不显示弹窗
        },
        createdAt: Date.now(),
        lastLoginAt: 0,
        settings: this.getDefaultSettings(),
      };
      
      this.users.set(adminUid, adminUser);
      this.saveUser(adminUser); // 保存admin用户
      this.savePassword(adminUid, 'admin123');
      this.accountManager.createAccount(adminUid, 100000); // 10万初始资金
      this.referralCodeManager.registerCode(adminCode, adminUid);
      
      logger.log(`✅ Default test accounts created: broker (UID: 10000001, Code: ${brokerCode}, Password: broker123), admin (UID: 10000000, Code: ${adminCode}, Password: admin123)`);
    } catch (error) {
      logger.error('Failed to create default accounts:', error);
    }
  }

  /**
   * 清理旧版本的用户数据
   */
  private clearOldUserData(): void {
    const keysToRemove: string[] = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('user_') || 
        key.startsWith('account_') || 
        key.startsWith('trading_settings_') ||
        key.startsWith('broker_settings_') ||
        key.startsWith('password_') ||
        key === 'default_accounts_created' ||
        key === 'current_session'
      )) {
        keysToRemove.push(key);
      }
    }
    
    keysToRemove.forEach(key => localStorage.removeItem(key));
    logger.log(`🧹 Cleared ${keysToRemove.length} old data entries`);
  }

  /**
   * 生成用户UID（8位数字）
   */
  private generateUID(): string {
    // 生成8位数字UID
    let uid: string;
    do {
      uid = Math.floor(10000000 + Math.random() * 90000000).toString();
    } while (this.users.has(uid)); // 确保唯一性
    return uid;
  }

  /**
   * 生成会话ID
   */
  private generateSessionId(): string {
    return 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * 生成Token
   */
  private generateToken(): string {
    return 'token_' + Date.now() + '_' + Math.random().toString(36).substr(2, 16);
  }

  /**
   * 保存密码（简单哈希）
   */
  private savePassword(uid: string, password: string): void {
    // 生产环境应使用 bcrypt 等更安全的哈希方法
    const hash = btoa(password + uid);  // 简单的 Base64 编码
    const key = `password_${uid}`;
    storage.set(key, hash);
  }

  /**
   * 验证密码
   */
  private verifyPassword(uid: string, password: string): boolean {
    const key = `password_${uid}`;
    const savedHash = storage.get<string>(key, '');
    const hash = btoa(password + uid);
    return hash === savedHash;
  }

  /**
   * 获取默认设置
   */
  private getDefaultSettings(): UserSettings {
    return {
      defaultLeverage: 10,
      riskWarning: true,
      chartTheme: 'dark',
      colorScheme: 'green-red',
      emailNotifications: false,
      priceAlerts: false
    };
  }

  /**
   * 保存用户
   */
  private saveUser(user: User): void {
    const key = `user_${user.uid}`;
    storage.set(key, user);
  }

  /**
   * 加载所有用户
   */
  private loadUsers(): void {
    try {
      // 方式1: 从新格式加载（users对象）
      const usersObject = storage.get<Record<string, User>>('users');
      if (usersObject) {
        Object.entries(usersObject).forEach(([uid, user]) => {
          // 兼容不同的用户ID字段名
          const userId = (user as any).userId || user.uid || uid;
          const userWithUid = {
            ...user,
            uid: userId,
            userId: userId,
          };
          this.users.set(userId, userWithUid as User);
        });
        logger.log(`📊 Loaded ${this.users.size} users from 'users' object`);
      }

      // 方式2: 从旧格式加载（user_xxx单独key）
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        // ⭐ 跳过 user_id（这是存储当前用户ID的key，不是用户对象）
        if (key && key.startsWith('user_') && key !== 'user_id') {
          const user = storage.get<User>(key);
          if (user && user.uid) {
            // 只有在新格式中不存在时才添加
            if (!this.users.has(user.uid)) {
              this.users.set(user.uid, user);
            }
          }
        }
      }
      
      logger.log(`📊 Total loaded ${this.users.size} users from storage`);
    } catch (error) {
      console.error('❌ [loadUsers] 加载失败', error);
      logger.error('Failed to load users:', error);
    }
  }

  /**
   * 保存会话
   */
  private saveSession(session: UserSession): void {
    storage.set('current_session', session);
  }

  /**
   * 恢复会话
   */
  private restoreSession(): void {
    try {
      const session = storage.get<UserSession>('current_session');
      
      if (session && session.expiresAt > Date.now()) {
        this.currentSession = session;
        logger.log(`✅ Session restored: ${session.uid}`);
      } else if (session) {
        logger.warn('Session expired, clearing...');
        this.clearSession();
      }
    } catch (error) {
      console.error('❌ [restoreSession] 恢复失败', error);
      logger.error('Failed to restore session:', error);
    }
  }

  /**
   * 清除会话
   */
  private clearSession(): void {
    localStorage.removeItem('current_session');
  }

  // ==================== 推荐系统方法 ====================

  /**
   * 获取推荐码管理器
   */
  getReferralCodeManager(): ReferralCodeManager {
    return this.referralCodeManager;
  }

  /**
   * 获取推荐人索引管理器
   */
  getReferrerIndexManager(): ReferrerIndexManager {
    return this.referrerIndexManager;
  }

  /**
   * 获取用户的主推荐码（优先返回自定义码）
   */
  getUserReferralCode(uid: string): string | null {
    const user = this.users.get(uid);
    if (!user) return null;

    return user.customReferralCode || user.referralCode;
  }

  /**
   * 设置推荐人（48小时内可设置）
   */
  setReferrer(uid: string, referrerCode: string): { success: boolean; error?: string } {
    const user = this.users.get(uid);
    if (!user) {
      return { success: false, error: '用户不存在' };
    }

    // ✅ 先检查48小时限制
    const hoursSinceRegistration = (Date.now() - user.createdAt) / (1000 * 60 * 60);
    const within48Hours = hoursSinceRegistration <= 48;

    // ✅ 如果已有推荐人
    const oldReferrerUid = user.profile?.referrerUid;
    if (oldReferrerUid) {
      // 48小时内：允许修改
      if (within48Hours) {
        logger.log(`⚠️ User ${uid} is modifying referrer within 48 hours`);
        // 继续执行，允许修改
      } else {
        // 超过48小时：禁止修改
        return { success: false, error: '已超过48小时，无法修改推荐人。如需更换，请先解除当前推荐人' };
      }
    } else {
      // 没有推荐人：检查48小时限制
      if (!within48Hours) {
        return { success: false, error: '注册超过48小时，无法设置推荐人' };
      }
    }

    // 验证推荐码
    let referrerUid = this.referralCodeManager.getUidByCode(referrerCode);
    
    // 如果推荐码不存在，尝试作为 UID 查找（兼容旧系统）
    if (!referrerUid && /^\d{8}$/.test(referrerCode)) {
      const referrerUser = this.getUserByUID(referrerCode);
      if (referrerUser) {
        referrerUid = referrerCode;
        logger.log(`✅ Referrer found by UID (legacy): ${referrerUid}`);
      }
    }
    
    if (!referrerUid) {
      return { success: false, error: '推荐码不存在' };
    }

    // 不能推荐自己
    if (referrerUid === uid) {
      return { success: false, error: '不能填写自己的推荐码' };
    }

    // ✅ 如果是修改推荐人，需要移除旧的推荐关系
    if (oldReferrerUid && oldReferrerUid !== referrerUid) {
      this.referrerIndexManager.removeReferredUser(oldReferrerUid, uid);
      logger.log(`🔄 Removed old referral: ${oldReferrerUid} → ${uid}`);
    }

    // 更新用户数据
    user.profile = {
      ...user.profile,
      referrerCode,
      referrerUid,
      referrerLockedAt: Date.now()
    };

    // 更新索引（仅在新推荐人或推荐人变更时添加）
    if (oldReferrerUid !== referrerUid) {
      this.referrerIndexManager.addReferredUser(referrerUid, uid);
      logger.log(`✅ Added new referral: ${referrerUid} → ${uid}`);
    }

    // 保存
    this.users.set(uid, user);
    this.saveUser(user);

    logger.log(`✅ Referrer set/updated: ${uid} → ${referrerUid} (${referrerCode})`);
    return { success: true };
  }

  /**
   * 解除推荐人（仅在超过48小时后可用）
   */
  clearReferrer(uid: string): { success: boolean; error?: string } {
    const user = this.users.get(uid);
    if (!user) {
      return { success: false, error: '用户不存在' };
    }

    // 检查是否有推荐人
    if (!user.profile?.referrerUid) {
      return { success: false, error: '当前没有推荐人' };
    }

    // 检查48小时限制
    const hoursSinceRegistration = (Date.now() - user.createdAt) / (1000 * 60 * 60);
    if (hoursSinceRegistration <= 48) {
      return { success: false, error: '48小时内不能解除推荐人，请直接修改' };
    }

    const oldReferrerUid = user.profile.referrerUid;

    // 移除推荐关系
    this.referrerIndexManager.removeReferredUser(oldReferrerUid, uid);

    // 清除用户数据
    user.profile = {
      ...user.profile,
      referrerCode: undefined,
      referrerUid: undefined,
      referrerLockedAt: undefined
    };

    // 保存
    this.users.set(uid, user);
    this.saveUser(user);

    logger.log(`✅ Referrer cleared: ${uid} removed from ${oldReferrerUid}`);
    return { success: true };
  }

  /**
   * 设置自定义推荐码
   */
  setCustomReferralCode(uid: string, customCode: string): { success: boolean; error?: string } {
    const result = this.referralCodeManager.setCustomCode(uid, customCode);

    if (result.success) {
      const user = this.users.get(uid);
      if (user) {
        user.customReferralCode = customCode;
        this.users.set(uid, user);
        this.saveUser(user);
        logger.log(`✅ Custom referral code set: ${uid} → ${customCode}`);
      }
    }

    return result;
  }

  /**
   * 获取被推荐用户列表
   */
  getReferredUsers(referrerUid: string): User[] {
    return this.referrerIndexManager.getReferredUsers(referrerUid, this);
  }

  /**
   * 获取推荐人数
   */
  getReferredUserCount(referrerUid: string): number {
    return this.referrerIndexManager.getReferredUserCount(referrerUid);
  }
}
